/*
Copyright (c) 2025 Mathilde Meyer, Naziha Beghdadi
Licensed under the MIT License (see LICENSE file for details)
*/
import React, { useEffect, useRef } from 'react';
import { DataSet, Timeline as VisTimeline } from 'vis-timeline/standalone';
import 'vis-timeline/styles/vis-timeline-graph2d.min.css';
import titles from "./titles";
import html2canvas from 'html2canvas';

const BASE_DATE = new Date("2025-04-01"); // "Jour 1"

function getDateFromDay(dayString) {
  const day = parseInt(dayString);
  if (isNaN(day)) return null;
  const date = new Date(BASE_DATE);
  date.setDate(BASE_DATE.getDate() + day - 1);
  return date.toISOString().split("T")[0];
}

const TimelineComponent = ({ data, index, projectName }) => {
  const containerRef = useRef(null); // cible de div qui contiendra la timeline
  const timelineRef = useRef(null); // désigne la timeline à download 
  const captureRef = useRef(null); // pour la légende

  const usedIconsMap = new Map();

  data.forEach((entry) => {
    entry.interventions?.forEach((intervention) => {
      const type = intervention.interventionType;
      const match = titles.find((t) => t.label === type);
      if (match && !usedIconsMap.has(type)) {
        usedIconsMap.set(type, match.icon);
      }
    });
  });

  const usedIcons = Array.from(usedIconsMap.entries());

  useEffect(() => {
    if (!containerRef.current) return; // vérifie référence au conteneur
    const items = [];
    data.forEach((inter, interIndex) => {

      const prepType = inter.preparation;
      const prepDays = parseInt(inter.preparationDays || "0");
      const acclimatation = inter.acclimatation;
      const acclimatationDays = parseInt(inter.acclimatationDays || "0");

      if(acclimatation && acclimatationDays > 0){
        const acclimStartDate = getDateFromDay(1);
        const acclimEndDate = getDateFromDay(acclimatationDays+1);
        if (acclimStartDate && acclimEndDate){
          items.push({
            id: `${interIndex}-acclim`,
            content: `
              <div style="display:flex; justify-content: center; align-items: center; height: 100%;">
                <span style="font-size: 0.75rem; font-weight: 500; background-color: #f0f0f0; padding: 4px 8px; border-radius: 6px;">
                  Acclimatation
                </span>
              </div>
            `,
            start: acclimStartDate,
            end: acclimEndDate,
            type: "range"
          })        
        }
      }

      if(prepType && prepDays > 0){
        let prepStartDay = 1; // commence au jour 1 par défaut
        if(acclimatation === "yes" && acclimatationDays > 0){
          prepStartDay = acclimatationDays+1;
        }
        const prepStartDate = getDateFromDay(prepStartDay);
        const prepEndDate = getDateFromDay(prepStartDay+prepDays);
        if(prepStartDate && prepEndDate) {
          items.push({
            id: `${interIndex}-prep`,
            content: `
              <div style="display:flex; justify-content: center; align-items: center; height: 100%;">
                <span style="font-size: 0.75rem; font-weight: 500; background-color: #f0f0f0; padding: 4px 8px; border-radius: 6px;">
                  ${prepType.replace("_", " ")}
                </span>
              </div>
            `,
            start: prepStartDate,
            end: prepEndDate,
            type: "range"
          });        
        }
      }

      inter.interventions.forEach((intervention, intIndex) => {
        const startDay = parseInt(intervention.daysStart);
        // interventions
        if (!isNaN(startDay)){
          const repeatType = intervention.repeatedIntervention;
          const xDays = parseInt(intervention.xDays);
          const yTimes = parseInt(intervention.yTimes);
          // every X day
          if (repeatType === "every_day" && !isNaN(xDays)) {
            for (let offset = 0; offset < xDays; offset++) {
              const day = startDay + offset;
              const date = getDateFromDay(day);
              const title = titles.find(t => t.label === intervention.interventionType);
              const icon = title?.icon || "";
              if (date) {
                items.push({
                  id: `${interIndex}-${intIndex}-r${offset}`,
                  content: `
                  <div style="display:flex; align-items: center; gap: 6px;">
                    ${icon ? `<img src="${icon}" width="20" height="20" alt="icon" />` : ""}
                  </div>
                `,
                  start: date,
                });
              }
            }
          // every X days for Y times
          }else if (repeatType === "every_x_days" && !isNaN(xDays) && !isNaN(yTimes)){
            const title = titles.find(t => t.label === intervention.interventionType);
            const icon = title?.icon || "";
            for (let i = 0; i< yTimes; i++){
              const day = startDay +i * xDays;
              const date = getDateFromDay(day);
              if (date){
                items.push({
                  id: `${interIndex}-${intIndex}-x${i}`,
                  content: `
                  <div style="display:flex; justify-content: center; align-items: center; height: 100%;">
                    ${icon ? `<img src="${icon}" width="24" height="24" alt="icon" />` : ""}
                  </div>
                  `,
                  start: date,
                });
              }
            }
          // after X days
          } else if (repeatType === "after_x_days" && !isNaN(xDays)){
            const dates = [getDateFromDay(startDay), getDateFromDay(startDay + xDays)];
            const title = titles.find(t => t.label === intervention.interventionType);
            const icon = title?.icon || "";
            dates.forEach((date,idx)=> {
              if (date) {
                items.push({
                  id: `${interIndex}-${intIndex}-after-${idx}`,
                  content: `
                    <div style="display:flex; justify-content: center; align-items: center; height: 100%;">
                      ${icon ? `<img src="${icon}" width="24" height="24" alt="icon" />` : ""}
                    </div>
                    `,
                  start: date,
                });
              }
            });        
          // si aucune répétition de l'intervention
          }else {
            const date = getDateFromDay(intervention.daysStart); // convertion de jour X en date
            if (date) {
              const title = titles.find(t => t.label === intervention.interventionType);
              const icon = title?.icon || "";
              items.push({
                id: `${interIndex}-${intIndex}`,
                content: `
                  <div style="display:flex; align-items: center; gap: 6px;">
                    ${icon ? `<img src="${icon}" width="20" height="20" alt="icon" />` : ""}
                  </div>
                `,
                start: date,
              });
            }
          }
        }
      })
    })

    const options = { // définition du comportement de la timeline
      showCurrentTime: false,
      zoomable: false,
      moveable: false,
      selectable: false,
      stack: true,
      showMajorLabels: false,
      showMinorLabels: true,
      orientation: 'top',
      timeAxis: {
        scale: 'day',
        step: 1,
        format: {
          minorLabels: {
            day: (date) => {
              const dayNumber = Math.floor((date - BASE_DATE) / (1000*60*60*24)) + 1;
              return `Jour ${dayNumber}`;
            }
          },
          majorLabels: {
            day: () => "", 
          }
        }
      }
    };
    // création
    const timeline = new VisTimeline(containerRef.current, new DataSet(items), options); // instancie et affiche timeline dans div
    // nettoyage 
    return () => {
      timeline.destroy();
    };
  }, [data]);

  const exportAsImage = () => {
    if(!timelineRef.current) return;
    html2canvas(captureRef.current).then((canvas) => {
      const link = document.createElement("a");
      const hasProjectName = projectName && projectName.trim() !== "";
      // nommage de la timeline téléchargé
      const safeName = hasProjectName
        ? projectName.trim().replace(/\s+/g, "-").toLowerCase()
        : null;

      const filename = hasProjectName
        ? `${safeName}Timeline-${index + 1}.png`
        : `Timeline-${index + 1}.png`;

      link.download = filename;

      link.href = canvas.toDataURL("image/png");
      link.click();
    });
  };

  return (
    <>
      <div ref={captureRef}>
        <div
          ref={timelineRef}
          style={{ // centre la timeline
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: "auto"
          }}
        >
          <div
            ref={containerRef}
            style={{ // contient la timeline visuelle
              width: '600px',
              height: '400px',
            }} 
          />
      </div>

      {usedIcons.length > 0 && (
        <div style={{
          marginTop: "12px",
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: "12px"
        }}>
          {usedIcons.map(([label, icon]) => (
            <div 
              key={label} 
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <img src={icon} alt={label} width={20} height={20} />
              <span style={{ fontSize: "0.85rem" }}>{label}</span>
            </div>
          ))}
        </div>
      )}
    </div>

    <div style={{ display: "flex", justifyContent: "center", gap: "16px", marginTop: "16px" }}>
      <button onClick={exportAsImage}> Download PNG</button>
    </div>
  </>
  );
};

export default TimelineComponent;
